// src/admin/screens/dashboard/DashboardScreen.tsx
// Admin landing page. Visual layout follows the Thandizo Admin Console design:
// queue tiles → KPI tiles → recent activity (real audit log) + system health.
//
// Data sources:
//   - useDashboardStats: 9 parallel Supabase counts (loans pending, KYC backlog,
//     delinquent, payments today, disbursed today, active loans, outstanding,
//     action requests, PTPs due).
//   - useAuditLog (capped to ~7 newest rows for the activity feed).
//
// The system-health card is intentionally a placeholder: there's no health
// endpoint wired yet. Marked with a comment so the next engineer can swap it
// for a real probe without confusion.
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Wallet, AlertTriangle, GitPullRequest,
  Download, ArrowRight, ArrowDown, Check, X, Zap, Flag, Settings, FilePlus,
} from 'lucide-react';
import { useDashboardStats } from '@admin/hooks/useDashboardStats';
import { useAuditLog } from '@admin/hooks/useAuditLog';
import { formatKwacha } from '@admin/utils/money';
import type { Ngwee } from '@/lib/types';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import StatusDot from '@admin/components/StatusDot';
import SectionHead from '@admin/components/SectionHead';
import AlertStrip from './AlertStrip';
import styles from './DashboardScreen.module.css';

export default function DashboardScreen() {
  const navigate = useNavigate();
  const { data: stats, isLoading, error } = useDashboardStats();
  const { data: audit } = useAuditLog();

  if (isLoading) return <div className={styles.state}>Loading dashboard…</div>;
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>Failed to load: {(error as Error).message}</div>
      </div>
    );
  }
  if (!stats) return <div className={styles.state}>No data.</div>;

  const alerts: { to: string; text: string }[] = [];
  if (stats.pending_approval > 0) {
    alerts.push({ to: '/loans?status=pending_approval', text: `${stats.pending_approval} loans awaiting approval` });
  }
  if (stats.kyc_pending > 0) {
    alerts.push({ to: '/kyc', text: `${stats.kyc_pending} KYC submissions to review` });
  }
  if (stats.action_requests_pending > 0) {
    alerts.push({ to: '/actions', text: `${stats.action_requests_pending} admin actions awaiting second-pair review` });
  }
  if (stats.ptp_due_today > 0) {
    alerts.push({ to: '/collections?ptp=today', text: `${stats.ptp_due_today} PTPs due today` });
  }

  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Portfolio overview"
        title="Today's book"
        description={`${today} · all figures in ZMW`}
        action={<Button variant="secondary" size="sm" leftIcon={<Download size={14} />}>Export report</Button>}
      />

      <AlertStrip alerts={alerts} />

      {/* Top strip — queues */}
      <div className={styles.queueGrid}>
        <QueueCard
          icon={<ShieldCheck size={18} strokeWidth={2} />}
          tone="warning"
          label="KYC to review"
          value={stats.kyc_pending}
          onClick={() => navigate('/kyc')}
        />
        <QueueCard
          icon={<Wallet size={18} strokeWidth={2} />}
          tone="brand"
          label="Loans pending"
          value={stats.pending_approval}
          onClick={() => navigate('/loans?status=pending_approval')}
        />
        <QueueCard
          icon={<AlertTriangle size={18} strokeWidth={2} />}
          tone="danger"
          label="In collections"
          value={stats.delinquent}
          onClick={() => navigate('/collections')}
        />
        <QueueCard
          icon={<GitPullRequest size={18} strokeWidth={2} />}
          tone="violet"
          label="Action requests"
          value={stats.action_requests_pending}
          onClick={() => navigate('/actions')}
        />
      </div>

      {/* KPI grid — book today */}
      <div className={styles.kpiGrid}>
        <KpiTile
          label="Disbursed today"
          value={formatKwacha(stats.disbursed_today_ngwee as Ngwee, { decimals: 0 })}
        />
        <KpiTile
          label="Outstanding"
          value={formatKwacha(stats.outstanding_ngwee as Ngwee, { decimals: 0 })}
        />
        <KpiTile
          label="Active loans"
          value={String(stats.active_loans)}
          delta="Updated just now"
        />
        <KpiTile
          label="Payments today"
          value={String(stats.payments_today)}
          delta={stats.payments_today === 0 ? 'No payments yet' : 'Reconciliation pending nightly'}
        />
      </div>

      {/* Recent activity + System health */}
      <div className={styles.twoUp}>
        <Card pad="flush">
          <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid var(--c-border-lt)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>Recent activity</h3>
            <Button
              variant="ghost"
              size="sm"
              rightIcon={<ArrowRight size={14} />}
              onClick={() => navigate('/audit')}
            >
              View full log
            </Button>
          </div>
          <div className={styles.activityList}>
            {audit && audit.length > 0 ? (
              audit.slice(0, 7).map((a) => (
                <div key={a.id} className={styles.activityRow}>
                  <div className={styles.activityIcon}>{iconForAction(a.action)}</div>
                  <div className={styles.activityText}>
                    <span className={styles.activityActor}>{a.admin_user?.display_name ?? a.admin_user?.email ?? 'System'}</span>{' '}
                    <span className={styles.activityVerb}>{verbForAction(a.action)}</span>{' '}
                    {a.target_id && <span className={styles.activityRef}>{shortenTarget(a.target_id)}</span>}
                  </div>
                  <span className={styles.activityTime}>{relativeTime(a.created_at)}</span>
                </div>
              ))
            ) : (
              <div className={styles.activityEmpty}>No activity yet today.</div>
            )}
          </div>
        </Card>

        {/* TODO: replace with a real health-probe RPC when the backend exposes one. */}
        <Card title="System health">
          <p style={{ margin: '0 0 16px', fontSize: 12.5, color: 'var(--c-text-muted)' }}>
            Integrations & services
          </p>
          <div className={styles.healthList}>
            <HealthRow name="Supabase Auth" tone="success" meta="—" />
            <HealthRow name="MTN MoMo API" tone="success" meta="—" />
            <HealthRow name="Airtel Money API" tone="success" meta="—" />
            <HealthRow name="SMS Gateway" tone="success" meta="—" />
            <HealthRow name="Reconciliation" tone="success" meta="—" />
          </div>
        </Card>
      </div>
    </div>
  );
}

interface QueueCardProps {
  icon: React.ReactNode;
  tone: 'brand' | 'warning' | 'danger' | 'violet';
  label: string;
  value: number;
  onClick: () => void;
}
function QueueCard({ icon, tone, label, value, onClick }: QueueCardProps) {
  return (
    <button type="button" className={styles.queueCard} onClick={onClick}>
      <span className={`${styles.queueIcon} ${styles[tone]}`}>{icon}</span>
      <span className={styles.queueText}>
        <span className={styles.queueLabel}>{label}</span>
        <div className={styles.queueValue}>{value}</div>
      </span>
      <ArrowRight size={14} className={styles.queueChev} />
    </button>
  );
}

function KpiTile({ label, value, delta }: { label: string; value: string; delta?: string }) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiTop}>
        <div className={styles.kpiText}>
          <div className={styles.kpiLabel}>{label}</div>
          <div className={styles.kpiValue}>{value}</div>
        </div>
      </div>
      {delta && <div className={styles.kpiDelta}>{delta}</div>}
    </div>
  );
}

function HealthRow({ name, tone, meta }: { name: string; tone: 'success' | 'warning' | 'danger'; meta: string }) {
  return (
    <div className={styles.healthRow}>
      <StatusDot tone={tone} />
      <span className={styles.healthName}>{name}</span>
      <span className={styles.healthMeta}>{meta}</span>
    </div>
  );
}

// ── Activity-feed helpers ────────────────────────────────────────────────
function iconForAction(action: string): React.ReactNode {
  if (action.startsWith('loan.approved')) return <Check size={14} />;
  if (action.startsWith('loan.rejected')) return <X size={14} />;
  if (action.startsWith('loan.disbursed')) return <Zap size={14} />;
  if (action.startsWith('kyc.')) return <ShieldCheck size={14} />;
  if (action.startsWith('borrower.flagged')) return <Flag size={14} />;
  if (action.startsWith('config.')) return <Settings size={14} />;
  if (action.includes('payment')) return <ArrowDown size={14} />;
  return <FilePlus size={14} />;
}

function verbForAction(action: string): string {
  // e.g. "loan.approved" → "approved loan"
  const [type, verb] = action.split('.');
  if (!verb) return action;
  return `${verb.replace(/_/g, ' ')} ${type}`;
}

function shortenTarget(id: string): string {
  // UUIDs / long ids are too noisy; show last 6 of any string >= 12 chars.
  if (id.length >= 12) return `…${id.slice(-6)}`;
  return id;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 45) return 'now';
  if (diff < 90) return '1m';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 7200) return '1h';
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
